import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { getBlockColor, getEmissiveColor, FIRE_COLORS } from './BlazeMaterial';

// ──────────── Dimensions ────────────
const BLOCK_SIZE = 0.88;
const BLOCK_DEPTH = BLOCK_SIZE * 0.75;
const HALF_DEPTH = BLOCK_DEPTH * 0.5;

// ──────────── Shared Geometries ────────────
const sharedBlockGeometry = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_DEPTH);
const sharedEdgeGeometry = new THREE.EdgesGeometry(
  new THREE.BoxGeometry(BLOCK_SIZE * 1.02, BLOCK_SIZE * 1.02, BLOCK_DEPTH * 1.02)
);

// Double-layered aura — inner bright + outer dim
const sharedInnerAura = new THREE.BoxGeometry(
  BLOCK_SIZE * 1.12, BLOCK_SIZE * 1.12, BLOCK_DEPTH * 0.55
);
const sharedOuterAura = new THREE.BoxGeometry(
  BLOCK_SIZE * 1.35, BLOCK_SIZE * 1.35, BLOCK_DEPTH * 0.4
);

// Fire heart — larger dodecahedron
const sharedHeartGeometry = new THREE.DodecahedronGeometry(0.26, 0);

// Magma veins — 3 cracks, wide and bold
const sharedVeinGeo = new THREE.PlaneGeometry(BLOCK_SIZE * 1.1, BLOCK_SIZE * 0.075);
const sharedVeinShortGeo = new THREE.PlaneGeometry(BLOCK_SIZE * 0.7, BLOCK_SIZE * 0.06);

// Vein glow halos — wider transparent planes behind veins
const sharedVeinHaloGeo = new THREE.PlaneGeometry(BLOCK_SIZE * 1.1, BLOCK_SIZE * 0.22);
const sharedVeinHaloShortGeo = new THREE.PlaneGeometry(BLOCK_SIZE * 0.7, BLOCK_SIZE * 0.18);

// Hot spot — bright center glow
const sharedHotSpot = new THREE.CircleGeometry(0.15, 10);

// Rising flame cones — 4 cones, varied sizes
const sharedFlameBig = new THREE.ConeGeometry(0.055, 0.24, 5);
const sharedFlameSmall = new THREE.ConeGeometry(0.035, 0.16, 5);

// Drifting ember sparks — tiny dots
const sharedEmberDot = new THREE.CircleGeometry(0.016, 5);

// ──────────── Flame cone configs ────────────
const FLAME_CONFIGS = [
  { x: -0.15, geo: 'big',   color: 'flame',     speed: 1.3, phase: 0 },
  { x: -0.03, geo: 'big',   color: 'flameCore', speed: 1.6, phase: 1.5 },
  { x:  0.1,  geo: 'small', color: 'flame',     speed: 1.9, phase: 3.2 },
  { x:  0.2,  geo: 'small', color: 'ember',     speed: 1.5, phase: 4.8 },
];

// ──────────── Ember spark configs ────────────
const EMBER_CONFIGS = [
  { x:  0.38, y: -0.1,  dir: 1,  speed: 1.2, phase: 0 },
  { x: -0.38, y:  0.05, dir: -1, speed: 1.5, phase: 1.7 },
  { x:  0.1,  y:  0.38, dir: 1,  speed: 1.8, phase: 3.1 },
  { x: -0.2,  y:  0.38, dir: -1, speed: 1.4, phase: 4.5 },
];

const EmberBlock3D = ({
  position,
  colorClass,
  isGhost = false,
  isActive = false,
  isPlaced = false,
  animate = true,
  scale = 1
}) => {
  const groupRef = useRef();
  const mainRef = useRef();
  const heartRef = useRef();
  const innerAuraRef = useRef();
  const outerAuraRef = useRef();
  const edgeRef = useRef();
  const vein1Ref = useRef();
  const vein2Ref = useRef();
  const vein3Ref = useRef();
  const veinH1Ref = useRef();
  const veinH2Ref = useRef();
  const veinH3Ref = useRef();
  const hotSpotRef = useRef();
  const flame0 = useRef();
  const flame1 = useRef();
  const flame2 = useRef();
  const flame3 = useRef();
  const spark0 = useRef();
  const spark1 = useRef();
  const spark2 = useRef();
  const spark3 = useRef();

  const phaseRef = useRef(Math.random() * Math.PI * 2);

  const baseColor = getBlockColor(colorClass);
  const emissiveColor = getEmissiveColor(colorClass);

  const flameRefs = [flame0, flame1, flame2, flame3];
  const sparkRefs = [spark0, spark1, spark2, spark3];
  const veinRefs = [vein1Ref, vein2Ref, vein3Ref];
  const veinHaloRefs = [veinH1Ref, veinH2Ref, veinH3Ref];

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const p = phaseRef.current;

    // ── Active breathing ──
    if (groupRef.current && animate && isActive) {
      groupRef.current.scale.setScalar(1 + Math.sin(t * 2.5 + p) * 0.025);
    }

    if (isGhost) return;

    // ── SURFACE HEAT — emissive color cycling through fire spectrum ──
    if (mainRef.current?.material) {
      const heat = Math.sin(t * 1.5 + p) * 0.35 + Math.sin(t * 3.7 + p * 1.3) * 0.15 + 0.5;
      const flicker = Math.sin(t * 9 + p) * 0.06;
      const burstChance = Math.sin(t * 17 + p * 3) > 0.92 ? 0.3 : 0; // rare bright burst

      mainRef.current.material.emissiveIntensity =
        (isActive ? 1.3 : 0.75) + heat * 0.6 + flicker + burstChance;

      // Emissive color shifts: dark red → bright orange-red based on heat
      const r = 0.55 + heat * 0.45;
      const g = 0.05 + heat * 0.2;
      const b = 0.02;
      mainRef.current.material.emissive.setRGB(r, g, b);
    }

    // ── FIRE HEART — larger, brighter, dramatic flicker ──
    if (heartRef.current) {
      heartRef.current.rotation.y = t * 0.8 + p;
      heartRef.current.rotation.x = Math.sin(t * 0.45 + p) * 0.7;
      heartRef.current.rotation.z = Math.cos(t * 0.3 + p) * 0.2;
      if (heartRef.current.material) {
        const f = Math.sin(t * 4 + p) * 0.15
              + Math.sin(t * 9.3 + p * 2) * 0.12
              + Math.sin(t * 15 + p * 0.7) * 0.06
              + (Math.sin(t * 23 + p) > 0.9 ? 0.15 : 0); // occasional bright flash
        const intensity = isActive ? 0.9 + f : 0.6 + f;
        heartRef.current.material.opacity = Math.max(0.35, Math.min(1.0, intensity));
        heartRef.current.material.emissiveIntensity = intensity * 4.5;
      }
    }

    // ── DOUBLE AURA — inner warm yellow, outer dim red ──
    if (innerAuraRef.current?.material) {
      innerAuraRef.current.material.opacity = isActive
        ? 0.38 + Math.sin(t * 2.5 + p) * 0.15
        : 0.22 + Math.sin(t * 1.5 + p) * 0.08;
    }
    if (outerAuraRef.current?.material) {
      outerAuraRef.current.material.opacity = isActive
        ? 0.22 + Math.sin(t * 1.8 + p + 0.5) * 0.1
        : 0.12 + Math.sin(t * 1.0 + p + 0.5) * 0.05;
    }

    // ── MAGMA VEINS — 3 cracks pulsing with color temperature shift ──
    const veinAngles = [Math.PI * 0.2, -Math.PI * 0.25, Math.PI * 0.08];
    veinRefs.forEach((ref, i) => {
      if (ref.current?.material) {
        const wave = Math.sin(t * (2.5 + i * 0.8) + p + i * 1.7);
        const pulse = isActive
          ? 0.7 + wave * 0.28
          : 0.38 + wave * 0.22;
        ref.current.material.opacity = pulse;
        // Color temp: orange → yellow → white-hot at peak
        const temp = wave * 0.5 + 0.5;
        ref.current.material.color.setRGB(
          1.0,
          0.55 + temp * 0.4,
          temp * 0.3
        );
      }
      // Vein halos follow same pulse but dimmer
      if (veinHaloRefs[i]?.current?.material) {
        const haloWave = Math.sin(t * (2.5 + i * 0.8) + p + i * 1.7);
        veinHaloRefs[i].current.material.opacity = isActive
          ? 0.2 + haloWave * 0.12
          : 0.1 + haloWave * 0.08;
      }
    });

    // ── HOT SPOT — dramatic center pulse with scale throb ──
    if (hotSpotRef.current?.material) {
      const spotHeat = Math.sin(t * 2.0 + p) * 0.5 + 0.5;
      const spotFlash = Math.sin(t * 7 + p) * 0.08;
      hotSpotRef.current.material.opacity = isActive
        ? 0.35 + spotHeat * 0.3 + spotFlash
        : 0.18 + spotHeat * 0.18;
      hotSpotRef.current.scale.setScalar(0.75 + spotHeat * 0.5);
    }

    // ── EDGE GLOW — pulsing ember orange ──
    if (edgeRef.current?.material) {
      const edgePulse = Math.sin(t * 2.8 + p);
      edgeRef.current.material.opacity = isActive
        ? 0.8 + edgePulse * 0.15
        : 0.5 + edgePulse * 0.12;
      // Edge color shifts slightly warmer at peak
      const eTemp = edgePulse * 0.5 + 0.5;
      edgeRef.current.material.color.setRGB(1.0, 0.27 + eTemp * 0.15, 0.0);
    }

    // ── RISING FLAME CONES (falling only) — 4 cones, layered ──
    if (!isPlaced) {
      flameRefs.forEach((ref, i) => {
        if (!ref.current) return;
        const cfg = FLAME_CONFIGS[i];

        const cycle = ((t * cfg.speed + p + cfg.phase) % 2.0) / 2.0;
        const rise = cycle * BLOCK_SIZE * 1.0;

        // Fade curve: fast appear, hold, then fade
        const alpha = Math.min(cycle * 5, 1) * Math.pow(Math.max(1 - cycle, 0), 0.8);

        ref.current.position.y = BLOCK_SIZE * 0.44 + rise;
        ref.current.position.x = cfg.x + Math.sin(t * 3.0 + cfg.phase) * 0.04;

        const s = (isActive ? 1.5 : 1.0) * (0.5 + Math.sin(cycle * Math.PI) * 0.7);
        ref.current.scale.set(s, s * 1.4, s);

        if (ref.current.material) {
          ref.current.material.opacity = isActive ? alpha * 0.8 : alpha * 0.5;
        }
      });
    }

    // ── DRIFTING EMBER SPARKS (falling only) — tiny dots floating off edges ──
    if (!isPlaced) {
      sparkRefs.forEach((ref, i) => {
        if (!ref.current) return;
        const cfg = EMBER_CONFIGS[i];

        const cycle = ((t * cfg.speed + p + cfg.phase) % 2.8) / 2.8;
        const drift = cycle * BLOCK_SIZE * 0.7;

        const alpha = Math.min(cycle * 4, 1) * Math.pow(Math.max(1 - cycle, 0), 1.5);

        // Drift outward/upward from the block edge
        const isVertical = Math.abs(cfg.y) > 0.3;
        ref.current.position.x = cfg.x * BLOCK_SIZE + (isVertical ? Math.sin(t * 2 + cfg.phase) * 0.05 : cfg.dir * drift);
        ref.current.position.y = cfg.y * BLOCK_SIZE + (isVertical ? drift : Math.sin(t * 1.5 + cfg.phase) * 0.06 + drift * 0.4);
        ref.current.position.z = HALF_DEPTH + 0.02;

        if (ref.current.material) {
          ref.current.material.opacity = isActive ? alpha * 0.85 : alpha * 0.5;
        }
        ref.current.scale.setScalar((isActive ? 1.3 : 0.9) * (0.4 + alpha * 0.8));
      });
    }
  });

  // ──────────── Materials ────────────
  const material = useMemo(() => {
    if (isGhost) {
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(baseColor),
        emissive: new THREE.Color(baseColor),
        emissiveIntensity: 0.4,
        metalness: 0.3,
        roughness: 0.2,
        opacity: 0.3,
        transparent: true,
        side: THREE.DoubleSide,
      });
    }

    // Volcanic obsidian — dark surface, hot emissive bleeding through
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color('#1a0508'),
      emissive: new THREE.Color(emissiveColor),
      emissiveIntensity: isActive ? 1.3 : 0.75,
      metalness: 0.78,
      roughness: 0.12,
      side: THREE.DoubleSide,
    });
  }, [baseColor, emissiveColor, isGhost, isActive]);

  return (
    <group position={position} ref={groupRef} scale={scale}>
      {/* ═══════ VOLCANIC OBSIDIAN BODY ═══════ */}
      <mesh
        ref={mainRef}
        geometry={sharedBlockGeometry}
        material={material}
        castShadow={!isGhost}
        receiveShadow={!isGhost}
      />

      {/* ═══════ FIRE HEART — large dodecahedron, slow tumble ═══════ */}
      {!isGhost && (
        <mesh ref={heartRef} geometry={sharedHeartGeometry}>
          <meshStandardMaterial
            color={FIRE_COLORS.flameCore}
            emissive={FIRE_COLORS.coreWhite}
            emissiveIntensity={4.0}
            transparent
            opacity={0.75}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* ═══════ DOUBLE AURA — inner warm + outer dim ═══════ */}
      {!isGhost && (
        <>
          <mesh ref={innerAuraRef} geometry={sharedInnerAura}>
            <meshBasicMaterial
              color={FIRE_COLORS.flame}
              transparent
              opacity={0.28}
              side={THREE.BackSide}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          <mesh ref={outerAuraRef} geometry={sharedOuterAura}>
            <meshBasicMaterial
              color={FIRE_COLORS.ember}
              transparent
              opacity={0.15}
              side={THREE.BackSide}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        </>
      )}

      {/* ═══════ MAGMA VEINS — 3 bold cracks with glow halos ═══════ */}
      {!isGhost && (
        <>
          {/* Vein 1 — main diagonal */}
          <mesh ref={veinH1Ref} position={[0, 0, HALF_DEPTH + 0.005]} rotation={[0, 0, Math.PI * 0.2]} geometry={sharedVeinHaloGeo}>
            <meshBasicMaterial color={FIRE_COLORS.ember} transparent opacity={0.12} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          <mesh ref={vein1Ref} position={[0, 0, HALF_DEPTH + 0.01]} rotation={[0, 0, Math.PI * 0.2]} geometry={sharedVeinGeo}>
            <meshBasicMaterial color={FIRE_COLORS.flameCore} transparent opacity={0.55} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>

          {/* Vein 2 — opposing diagonal */}
          <mesh ref={veinH2Ref} position={[0, 0, HALF_DEPTH + 0.005]} rotation={[0, 0, -Math.PI * 0.25]} geometry={sharedVeinHaloGeo}>
            <meshBasicMaterial color={FIRE_COLORS.ember} transparent opacity={0.1} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          <mesh ref={vein2Ref} position={[0, 0, HALF_DEPTH + 0.01]} rotation={[0, 0, -Math.PI * 0.25]} geometry={sharedVeinGeo}>
            <meshBasicMaterial color={FIRE_COLORS.flame} transparent opacity={0.48} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>

          {/* Vein 3 — shorter horizontal branch */}
          <mesh ref={veinH3Ref} position={[0.05, -0.08, HALF_DEPTH + 0.005]} rotation={[0, 0, Math.PI * 0.08]} geometry={sharedVeinHaloShortGeo}>
            <meshBasicMaterial color={FIRE_COLORS.ember} transparent opacity={0.08} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
          <mesh ref={vein3Ref} position={[0.05, -0.08, HALF_DEPTH + 0.01]} rotation={[0, 0, Math.PI * 0.08]} geometry={sharedVeinShortGeo}>
            <meshBasicMaterial color={FIRE_COLORS.spark} transparent opacity={0.4} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
        </>
      )}

      {/* ═══════ HOT SPOT — bright center surface glow ═══════ */}
      {!isGhost && (
        <mesh ref={hotSpotRef} position={[0, 0, HALF_DEPTH + 0.015]} geometry={sharedHotSpot}>
          <meshBasicMaterial
            color={FIRE_COLORS.flameCore}
            transparent
            opacity={0.3}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* ═══════ EMBER EDGE GLOW ═══════ */}
      {!isGhost && (
        <lineSegments ref={edgeRef} geometry={sharedEdgeGeometry}>
          <lineBasicMaterial
            color={FIRE_COLORS.ember}
            linewidth={2}
            opacity={0.65}
            transparent
            blending={THREE.AdditiveBlending}
          />
        </lineSegments>
      )}

      {/* ═══════ RISING FLAME CONES (falling only) ═══════ */}
      {!isGhost && !isPlaced && FLAME_CONFIGS.map((cfg, i) => (
        <mesh
          key={`flame-${i}`}
          ref={flameRefs[i]}
          position={[cfg.x, BLOCK_SIZE * 0.44, HALF_DEPTH]}
          geometry={cfg.geo === 'big' ? sharedFlameBig : sharedFlameSmall}
        >
          <meshBasicMaterial
            color={FIRE_COLORS[cfg.color]}
            transparent
            opacity={0.55}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* ═══════ DRIFTING EMBER SPARKS (falling only) ═══════ */}
      {!isGhost && !isPlaced && EMBER_CONFIGS.map((cfg, i) => (
        <mesh
          key={`spark-${i}`}
          ref={sparkRefs[i]}
          position={[cfg.x * BLOCK_SIZE, cfg.y * BLOCK_SIZE, HALF_DEPTH + 0.02]}
          geometry={sharedEmberDot}
        >
          <meshBasicMaterial
            color={i < 2 ? FIRE_COLORS.coreWhite : FIRE_COLORS.spark}
            transparent
            opacity={0.6}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

export default EmberBlock3D;
